import { SavedViewsMenu } from '@/features/saved-views/components/saved-views-menu.js';
import type { SavedViewState } from '@/features/saved-views/lib/saved-views-client.js';
import type { ReportDetail } from '@/lib/api/reports-client.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';

interface ReportHeaderProps {
  detail: ReportDetail;
  locale?: string;
  onExport?: () => void;
  currentState?: SavedViewState;
  onLoadView?: (state: SavedViewState) => void;
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

function CapabilityBadge({ level }: { level: string }) {
  const label =
    level === 'manage'
      ? t('catalog.capability_manage')
      : level === 'execute'
        ? t('catalog.capability_execute')
        : t('catalog.capability_view');

  const colorClass =
    level === 'manage'
      ? 'bg-[var(--color-qup-primary)]/15 text-[var(--color-qup-primary)]'
      : level === 'execute'
        ? 'bg-[var(--color-qup-success)]/15 text-[var(--color-qup-success)]'
        : 'bg-[var(--color-qup-border-dark)]/60 text-[var(--color-qup-text-muted-dark)]';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        colorClass,
      )}
    >
      {label}
    </span>
  );
}

function VersionUpdateBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--color-qup-warning)]/15 px-2 py-0.5 text-[11px] font-medium text-[var(--color-qup-warning)]">
      {t('catalog.version_update_available')}
    </span>
  );
}

export function ReportHeader({
  detail,
  locale = 'es',
  onExport,
  currentState,
  onLoadView,
}: ReportHeaderProps) {
  const def = detail.definition as Record<string, unknown>;
  const titleRecord = def.title as Record<string, string> | undefined;
  const descRecord = def.description as Record<string, string> | undefined;

  const title = titleRecord?.[locale] ?? titleRecord?.es ?? detail.code;
  const description = descRecord?.[locale] ?? descRecord?.es;

  const lastExec = detail.lastExecution;

  return (
    <div
      className={cn(
        'sticky top-0 z-20 rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
        'bg-[var(--color-qup-bg-card-dark)] px-5 py-4 shadow-[var(--shadow-card)]',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-bold text-[var(--color-qup-text-dark)] leading-snug">
              {title}
            </h1>
            <CapabilityBadge level={detail.capabilities.level} />
            {detail.versionStatus === 'update_available' && <VersionUpdateBadge />}
          </div>

          {description !== undefined && (
            <p className="text-sm text-[var(--color-qup-text-muted-dark)] line-clamp-2">
              {description}
            </p>
          )}

          {lastExec !== undefined && (
            <p className="mt-1 text-xs text-[var(--color-qup-text-muted-dark)]">
              {interpolate(t('report.last_execution'), {
                when: new Date(lastExec.executedAt).toLocaleString('es-ES'),
              })}
              {' · '}
              {interpolate(t('report.rows_count'), { n: lastExec.rowCount.toLocaleString('es') })}
            </p>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {currentState !== undefined && onLoadView !== undefined && (
            <SavedViewsMenu
              tenantReportId={detail.tenantReportId}
              currentState={currentState}
              canSaveSharedView={detail.capabilities.canSaveSharedView}
              onLoad={onLoadView}
            />
          )}

          {detail.capabilities.canExport && onExport !== undefined && (
            <button
              type="button"
              onClick={onExport}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[var(--radius-button)]',
                'border border-[var(--color-qup-border-dark)] px-3 py-2 text-sm font-medium',
                'text-[var(--color-qup-text-dark)] hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
              )}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
                <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
              </svg>
              {t('report.export')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
